import { useEffect, useRef } from 'react';

import { extractArtifactInputsFromMessages } from '@erikdakoda/artifact/shared/extractArtifactInputsFromMessages';
import type { AiMessageForUi } from '@erikdakoda/convo/types';
import {
  buildArtifactInputsSyncKey,
  syncArtifactStoreFromMessageInputs,
} from '../shared/syncArtifactStoreFromMessageInputs';

type UseSyncArtifactStoreFromMessagesParams = {
  messages: readonly AiMessageForUi[];
  enabled?: boolean;
};

/**
 * Keeps the client artifact store aligned with the latest artifact tool inputs in convo messages.
 */
export function useSyncArtifactStoreFromMessages({
  messages,
  enabled = true,
}: UseSyncArtifactStoreFromMessagesParams): void {
  const syncedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    syncedKeyRef.current = null;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || messages.length === 0) {
      return;
    }

    const artifacts = extractArtifactInputsFromMessages(messages);
    if (artifacts.length === 0) {
      syncedKeyRef.current = null;
      return;
    }

    const syncKey = buildArtifactInputsSyncKey(artifacts);
    if (syncedKeyRef.current === syncKey) {
      return;
    }

    syncedKeyRef.current = syncKey;
    syncArtifactStoreFromMessageInputs(artifacts);
  }, [enabled, messages]);
}
